import { useEffect, useState } from 'react';
import Spinner from '../components/Spinner';
import { devGetMe, devGetUsageSummary, UsageSummary } from '../api/dev';

export default function DevApiCredit() {
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [meName, setMeName] = useState<string>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function refresh() {
    setLoading(true); setError('');
    try {
      const me = await devGetMe();
      setMeName(me.name || '');
      setSummary(await devGetUsageSummary());
    } catch (e: any) { setError(e?.message || String(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div>
      <h2>API Credit</h2>
      {loading && <Spinner label="Loading..." />}
      {error && <div style={{ color: 'crimson', marginBottom: 8 }}>Error: {error}</div>}
      {summary && (
        <div className="card">
          <div style={{ marginBottom: 6 }}>
            <div className="muted">Tenant</div>
            <div>{meName || '-'}</div>
            <div className="muted" style={{ fontSize: 12 }}>ID: <code>{summary.tenantId}</code></div>
          </div>
          <div className="row" style={{ gap: 16 }}>
            <div>
              <div className="muted">Month</div>
              <div><code>{formatMonthWords(summary.month)}</code></div>
            </div>
            <div>
              <div className="muted">Monthly Limit</div>
              <div>{summary.perMonthMax ?? '—'}</div>
            </div>
            <div>
              <div className="muted">Used (month)</div>
              <div>{summary.usedMonth}</div>
            </div>
            <div>
              <div className="muted">Remaining</div>
              <div>{summary.remainingMonth ?? '—'}</div>
            </div>
            <div>
              <div className="muted">Default RPM</div>
              <div>{summary.perMinuteDefault ?? 100}</div>
            </div>
          </div>
          <div style={{ height: 12 }} />
          <div>
            <div className="muted" style={{ marginBottom: 6 }}>Per-Service Overrides</div>
            <table className="tbl">
              <thead><tr><th>Service</th><th>RPM</th><th>Used (month)</th></tr></thead>
              <tbody>
                {Object.keys(summary.perService || {}).length === 0 && (
                  <tr><td colSpan={3}><span className="muted">None</span></td></tr>
                )}
                {Object.entries(summary.perService || {}).map(([svc, cfg]) => (
                  <tr key={svc}>
                    <td>{labelForService(svc)}</td>
                    <td>{cfg?.perMinute ?? '—'}</td>
                    <td>{(summary.byService || {})[svc] ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ height: 12 }} />
          <button className="btn" onClick={refresh}>Refresh</button>
        </div>
      )}
    </div>
  );
}

function labelForService(id: string): string {
  switch (id) {
    case 'api-credit': return 'API Credit Service';
    case 'current': return 'Current API';
    case 'forecast-hourly': return 'Forecast Hourly API';
    case 'forecast-daily': return 'Forecast Daily';
    case 'history': return 'History API';
    case 'astronomical': return 'Astronomical API';
    case 'bulk-current': return 'Bulk Current';
    case 'bulk-forecast-hourly': return 'Bulk Forecast Hourly';
    case 'forecast-push': return 'Forecast Push Notifications';
    default: return id;
  }
}

function formatMonthWords(yyyymm?: string): string {
  const s = String(yyyymm || '');
  if (s.length === 6) {
    const y = Number(s.slice(0, 4));
    const m = Number(s.slice(4, 6));
    if (!isNaN(y) && !isNaN(m) && m >= 1 && m <= 12) {
      try {
        const d = new Date(Date.UTC(y, m - 1, 1));
        return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
      } catch {}
    }
  }
  // Fallback
  try {
    const now = new Date();
    return now.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  } catch {
    return yyyymm || '';
  }
}
